
import { useState } from 'react';
import { useWarranty } from './hooks/useWarranty';
import { exportToExcel } from '../reports/lib/excelExport';
import { generateWarrantyPDF } from './lib/warrantyPDF';

export default function WarrantyReports() {
  const { claims, repairs, replacements, warrantyReports } = useWarranty();
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  if (claims.isLoading) return <div className="p-6">Loading warranty reports...</div>;

  const exportExcelReport = () => {
    exportToExcel('Warranty_Claims_Report', [
      {
        name: 'Claims',
        headers: ['Order', 'Product', 'Status', 'Customer', 'Purchase Date', 'Issue'],
        data: claims.data?.map((c: any) => [
          c.orderNo,
          c.product,
          c.status,
          c.customer,
          c.purchaseDate,
          c.issueDescription
        ]) || []
      }
    ]);
  };

  const getStatusStats = () => {
    const total = claims.data?.length || 0;
    const pending = claims.data?.filter((c: any) => c.status === 'pending').length || 0;
    const approved = claims.data?.filter((c: any) => c.status === 'approved').length || 0;
    const rejected = claims.data?.filter((c: any) => c.status === 'rejected').length || 0;
    const completed = claims.data?.filter((c: any) => ['repaired', 'replaced'].includes(c.status)).length || 0;

    return { total, pending, approved, rejected, completed };
  };

  const stats = getStatusStats();

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-[var(--color-primary)]">Warranty Reports</h1>
        <div className="flex space-x-2">
          <button 
            onClick={exportExcelReport}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
          >
            Export Excel
          </button>
          <button 
            onClick={() => claims.data?.[0] && generateWarrantyPDF(claims.data[0])}
            className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
          >
            Export PDF
          </button>
        </div>
      </div>

      {/* Statistics Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <h3 className="text-sm font-medium text-gray-600">Total Claims</h3>
          <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <h3 className="text-sm font-medium text-gray-600">Pending</h3>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <h3 className="text-sm font-medium text-gray-600">Approved</h3>
          <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
          <h3 className="text-sm font-medium text-gray-600">Rejected</h3>
          <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <h3 className="text-sm font-medium text-gray-600">Completed</h3>
          <p className="text-2xl font-bold text-purple-600">{stats.completed}</p>
        </div>
      </div>

      {/* Financial Impact */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2">Repair Costs</h3>
          <p className="text-2xl font-bold text-orange-600">
            ₹{repairs.data?.reduce((sum: number, r: any) => sum + (r.cost || 0), 0).toLocaleString() || '0'}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2">Active Repairs</h3>
          <p className="text-2xl font-bold text-blue-600">
            {repairs.data?.filter((r: any) => r.status === 'in-progress').length || 0}
          </p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2">Replacements</h3>
          <p className="text-2xl font-bold text-green-600">
            {replacements.data?.length || 0}
          </p>
        </div>
      </div>

      {/* Claims Status Distribution */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Claims Status Distribution</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Pending</span>
            <div className="flex items-center space-x-2">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-yellow-500 h-2 rounded-full" 
                  style={{ width: `${stats.total > 0 ? (stats.pending / stats.total) * 100 : 0}%` }}
                ></div>
              </div>
              <span className="text-sm text-gray-600">{stats.pending}</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Approved</span>
            <div className="flex items-center space-x-2">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full" 
                  style={{ width: `${stats.total > 0 ? (stats.approved / stats.total) * 100 : 0}%` }}
                ></div>
              </div>
              <span className="text-sm text-gray-600">{stats.approved}</span>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Completed</span>
            <div className="flex items-center space-x-2">
              <div className="w-32 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-purple-500 h-2 rounded-full" 
                  style={{ width: `${stats.total > 0 ? (stats.completed / stats.total) * 100 : 0}%` }}
                ></div>
              </div>
              <span className="text-sm text-gray-600">{stats.completed}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Claims Table */}
      <div className="bg-white p-4 rounded shadow">
        <h2 className="font-semibold mb-4">All Warranty Claims</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded shadow">
            <thead className="bg-[var(--color-accent)]/40">
              <tr>
                <th className="th">Order</th>
                <th className="th">Customer</th>
                <th className="th">Product</th>
                <th className="th">Status</th>
                <th className="th">Purchase Date</th>
                <th className="th">Issue</th>
                <th className="th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {claims.data?.map((c: any) => (
                <tr key={c.id} className="hover:bg-gray-50">
                  <td className="td">{c.orderNo}</td>
                  <td className="td">{c.customer}</td>
                  <td className="td">{c.product}</td>
                  <td className="td">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      c.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      c.status === 'approved' ? 'bg-green-100 text-green-800' :
                      c.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="td">{new Date(c.purchaseDate).toLocaleDateString()}</td>
                  <td className="td">{c.issueDescription?.substring(0, 50)}...</td>
                  <td className="td">
                    <button
                      onClick={() => generateWarrantyPDF(c)}
                      className="text-[var(--color-primary)] hover:text-[var(--color-primary)]/80 text-xs"
                    >
                      PDF
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
